import { CompanyTag } from "@/components/domain/CompanyTag";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  type DeliveryStatus,
  type DeliveryNoteSummaryDto,
  type Paginated,
} from "@hisense/shared";
import { useAuth } from "@/app/auth";
import { useCan } from "@/app/access";
import { useDeliveryNotes, useCustomers } from "@/api/hooks";
import { ExportButton } from "@/components/domain/ExportButton";
import { PageBody } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { SearchableFilter } from "@/components/ui/SearchableFilter";
import { Badge } from "@/components/ui/Badge";
import {
  ListTableFrame,
  EmptyTableRow,
  LIST_PAGE_BODY,
} from "@/components/ui/ListTable";
import { Td, Th, Tr } from "@/components/ui/Table";
import { useClampListPage, useListQueryState } from "@/lib/listQuery";
import { apiFetch } from "@/lib/api";
import { formatDate } from "@/domain/datetime";
import { translateStatus } from "@/domain/i18n";
const statuses: DeliveryStatus[] = [
  "Pending",
  "In Transit",
  "Delivered",
  "Cancelled",
];
export function DeliveryNotesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const can = useCan();
  const navigate = useNavigate();
  const list = useListQueryState();
  const customers = useCustomers({});
  const [error, _setError] = useState<string | null>(null);
  const context = list.searchParams.size ? `?${list.searchParams}` : "";
  const archived = list.searchParams.get("archived") === "true";
  const from = list.searchParams.get("from") ?? "";
  const to = list.searchParams.get("to") ?? "";
  const status = list.searchParams.get("status");
  const refType = list.searchParams.get("refType");
  const filters = {
    q: list.q.trim() || undefined,
    page: list.page,
    pageSize: list.pageSize,
    status: statuses.includes(status as DeliveryStatus)
      ? (status as DeliveryStatus)
      : undefined,
    refType:
      refType === "packing"
        ? ("packing" as const)
        : refType === "invoice"
          ? ("invoice" as const)
          : undefined,
    customerId: list.searchParams.get("customerId") || undefined,
    from: from ? new Date(from + "T00:00:00").getTime() : undefined,
    to: to ? new Date(to + "T23:59:59.999").getTime() : undefined,
    sort: list.searchParams.get("sort") || "-date",
    archived: archived ? ("true" as const) : ("false" as const),
  };
  const result = useDeliveryNotes(filters);
  useClampListPage(result.data?.total, list.clampPage);
  const rows = result.data?.data ?? [];
  const customerOptions = (customers.data?.data ?? []).map((c) => ({
    value: c.id,
    label: c.companyName,
  }));
  if (
    filters.customerId &&
    !customerOptions.some((c) => c.value === filters.customerId)
  ) {
    customerOptions.push({
      value: filters.customerId,
      label: rows[0]?.billToName || t("deliveryWorkspace.unavailableCustomer"),
    });
  }
  return (
    <PageBody className={LIST_PAGE_BODY}>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Input
          aria-label={t("common.search")}
          placeholder={t("common.search")}
          {...list.searchInput}
          className="w-full max-w-xs"
        />
        <Select
          aria-label={t("deliveryWorkspace.lifecycle")}
          value={archived ? "archived" : "active"}
          onChange={(e) =>
            list.updateParam(
              "archived",
              e.target.value === "archived" ? "true" : null,
            )
          }
        >
          <option value="active">{t("invoiceWorkspace.active")}</option>
          <option value="archived">{t("invoiceWorkspace.archived")}</option>
        </Select>
        <div className="ml-auto">
          <ExportButton<DeliveryNoteSummaryDto>
            name="delivery-notes"
            title={t("nav.deliveryNotes")}
            rows={rows}
            total={result.data?.total}
            disabled={result.isFetching}
            errorKey="deliveryNotes.errorLoad"
            fetchPage={(page, pageSize, signal) =>
              apiFetch<Paginated<DeliveryNoteSummaryDto>>("/delivery-notes", {
                query: { ...filters, page, pageSize },
                signal,
              })
            }
            columns={[
              { header: t("deliveryNotes.noteNo"), value: (r) => r.dnNo },
              { header: t("common.customer"), value: (r) => r.billToName },
              { header: t("common.shipTo"), value: (r) => r.shipTo },
              { header: t("common.date"), value: (r) => formatDate(r.date) },
              { header: t("deliveryWorkspace.receiptDate"), value: (r) => formatDate(r.deliveryDate) },
              { header: t("common.status"), value: (r) => translateStatus(t, r.deliveryStatus) },
              { header: t("invoices.piNo"), value: (r) => r.piNo },
              { header: t("packingLists.listNo"), value: (r) => r.plNo },
              { header: t("packingLists.invoiceNo"), value: (r) => r.invoiceNo },
              {
                header: t("deliveryWorkspace.plannedReceived"),
                value: (r) =>
                  r.quantities?.map((q) => `${q.planned} / ${q.received} ${q.unit}`).join("; "),
              },
            ]}
          />
        </div>
        {user && can("records:create") && (
          <Button
            size="sm"
            onClick={() => navigate(`/delivery-notes/new${context}`)}
          >
            {t("deliveryNotes.newDeliveryNote")}
          </Button>
        )}
      </div>
      <div className="flex shrink-0 flex-wrap gap-2">
        <Select
          aria-label={t("common.status")}
          value={filters.status ?? ""}
          onChange={(e) => list.updateParam("status", e.target.value || null)}
        >
          <option value="">{t("common.all")}</option>
          {statuses.map((s) => (
            <option value={s} key={s}>
              {translateStatus(t, s)}
            </option>
          ))}
        </Select>
        <Select
          aria-label={t("deliveryNotes.refType")}
          value={filters.refType ?? ""}
          onChange={(e) => list.updateParam("refType", e.target.value || null)}
        >
          <option value="">{t("common.all")}</option>
          <option value="packing">{t("deliveryNotes.fromPacking")}</option>
          <option value="invoice">{t("deliveryNotes.fromInvoice")}</option>
        </Select>
        <SearchableFilter
          label={t("common.customer")}
          allLabel={t("common.all")}
          searchPlaceholder={t("common.search")}
          value={filters.customerId ?? ""}
          options={customerOptions}
          onChange={(value) => list.updateParam("customerId", value || null)}
        />
        <Input
          type="date"
          aria-label={t("deliveryWorkspace.from")}
          value={from}
          onChange={(e) => list.updateParam("from", e.target.value || null)}
        />
        <Input
          type="date"
          aria-label={t("deliveryWorkspace.to")}
          value={to}
          onChange={(e) => list.updateParam("to", e.target.value || null)}
        />
        <Select
          aria-label={t("piWorkspace.sort")}
          value={filters.sort}
          onChange={(e) => list.updateParam("sort", e.target.value)}
        >
          <option value="-date">{t("piWorkspace.newest")}</option>
          <option value="date">{t("piWorkspace.oldest")}</option>
          <option value="dnNo">{t("deliveryNotes.noteNo")} ↑</option>
          <option value="-dnNo">{t("deliveryNotes.noteNo")} ↓</option>
        </Select>
      </div>
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      <ListTableFrame
        isLoading={result.isLoading}
        isFetching={result.isFetching}
        error={result.error}
        errorMessage={t("deliveryNotes.errorLoad")}
        onRetry={() => void result.refetch()}
        page={result.data?.page ?? list.page}
        pageSize={result.data?.pageSize ?? list.pageSize}
        total={result.data?.total ?? 0}
        onPageChange={list.updatePage}
        onPageSizeChange={list.updatePageSize}
      >
        <thead>
          <tr>
            <Th>#</Th>
            <Th pin>{t("deliveryNotes.noteNo")}</Th>
            <Th>{t("common.status")}</Th>
            <Th>{t("common.customer")}</Th>
            <Th>{t("common.shipTo")}</Th>
            <Th>{t("common.reference")}</Th>
            <Th>{t("common.date")}</Th>
            <Th>{t("deliveryWorkspace.receiptDate")}</Th>
            <Th>{t("deliveryWorkspace.plannedReceived")}</Th>
            <Th>{t("pi.respBy")}</Th>
            <Th pinRight>{t("common.actions")}</Th>
          </tr>
        </thead>
        <tbody>
          {!rows.length && (
            <EmptyTableRow colSpan={11}>{t("common.noMatches")}</EmptyTableRow>
          )}
          {rows.map((r, i) => (
            <Tr key={r.id}>
              <Td className="font-mono text-xs text-ink-faint">
                {(list.page - 1) * list.pageSize + i + 1}
              </Td>
              <Td pin>
                <Link
                  className="font-mono text-brand underline"
                  to={`/delivery-notes/${r.id}${context}`}
                >
                  {r.dnNo}
                </Link>
              </Td>
              <Td>
                <Badge
                  tone={
                    r.deliveryStatus === "Delivered"
                      ? "ok"
                      : r.deliveryStatus === "In Transit"
                        ? "info"
                        : r.deliveryStatus === "Cancelled"
                          ? "danger"
                          : "neutral"
                  }
                >
                  {translateStatus(t, r.deliveryStatus)}
                </Badge>
              </Td>
              <Td>
                <p
                  className="max-w-56 truncate text-xs"
                  title={r.billToName ?? undefined}
                >
                  <CompanyTag region={r.customerRegion} className="mr-1.5" />
                  {r.billToName || "—"}
                </p>
              </Td>
              <Td>
                <p
                  className="max-w-56 truncate text-xs"
                  title={r.shipTo ?? undefined}
                >
                  {r.shipTo
                    ?.split("\n")
                    .map((line) => line.trim())
                    .find((line) => line && line !== r.billToName?.trim()) ||
                    r.shipTo ||
                    "—"}
                </p>
              </Td>
              <Td className="font-mono text-xs">
                <p>{r.piNo}</p>
                <p>{r.plNo ?? r.invoiceNo}</p>
              </Td>
              <Td className="font-mono text-xs">{formatDate(r.date)}</Td>
              <Td className="font-mono text-xs">
                {formatDate(r.deliveryDate)}
              </Td>
              <Td className="font-mono text-xs">
                {r.quantities?.map((q) => (
                  <p key={q.unit}>
                    {q.planned} / {q.received} {q.unit}
                  </p>
                ))}
              </Td>
              <Td className="text-xs">{r.respBy || "—"}</Td>
              <Td pinRight>
                <Link
                  className="text-xs text-brand underline"
                  to={`/delivery-notes/${r.id}${context}`}
                >
                  {t("invoiceWorkspace.view")}
                </Link>
              </Td>
            </Tr>
          ))}
        </tbody>
      </ListTableFrame>
    </PageBody>
  );
}
